import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAPI } from '../hooks/useAPI';
import Notification from '../components/Notification';
import ConfirmDialog from '../components/ConfirmDialog';
import APP_CONFIG from '../config/appConfig';

const Poetry = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('edit') === 'true';
  const { data: poems, loading, error, deleteItem } = useAPI('poems');
  const [selectedPoem, setSelectedPoem] = useState(null);
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    poem: null,
    title: '',
    message: '',
    confirmText: 'Delete',
    cancelText: 'Cancel'
  });

  // Ensure poems is always an array
  const safePoems = Array.isArray(poems) ? poems : [];

  const handleDelete = async (poem) => {
    setConfirmDialog({
      isOpen: true,
      poem: poem,
      title: 'Delete Poem',
      message: `Are you sure you want to delete "${poem.title}"? This action cannot be undone.`
    });
  };

  const confirmDelete = async () => {
    try {
      const poemId = confirmDialog.poem.id || confirmDialog.poem._id;
      await deleteItem(poemId);
      setNotification({ message: 'Poem deleted successfully!', type: 'success' });
    } catch (error) {
      setNotification({ message: 'Failed to delete poem. Please try again.', type: 'error' });
    } finally {
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const cancelDelete = () => {
    setConfirmDialog({ ...confirmDialog, isOpen: false });
  };

  const handleEdit = (poem) => {
    window.location.href = `/admin/poetry?edit=${poem.id}`;
  };

  const truncateText = (text, maxLines = 4) => {
    if (!text) return { text: '', showReadMore: false };
    
    const lines = text.split('\n').filter(line => line.trim() !== '');
    
    if (lines.length <= maxLines) {
      return { text, showReadMore: false };
    }
    
    const truncatedLines = lines.slice(0, maxLines);
    const result = truncatedLines.join('\n');
    return {
      text: result,
      showReadMore: true
    };
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && selectedPoem) {
        setSelectedPoem(null);
      }
    };

    if (selectedPoem) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [selectedPoem]);

  if (loading && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading poetry...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-7xl mb-6 script-font text-white" style={{ fontSize: 'clamp(3rem, 8vw, 5rem)' }}>
            {isEditMode ? `Edit ${t('poetry.title')}` : t('poetry.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {isEditMode 
              ? `Manage your poetry collection (${safePoems.length} total)`
              : t('poetry.subtitle')
            }
          </p>
          {isEditMode && (
            <div className="mt-4">
              <a
                href="/admin/poetry"
                className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-white"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Poem
              </a>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {safePoems.map((poem) => {
            const lines = poem.content.split('\n').filter(line => line.trim() !== '');
            const words = poem.content.split(' ').filter(word => word.trim() !== '');
            const shouldShowReadMore = lines.length > 4 || words.length > 50;
            const displayText = shouldShowReadMore ? lines.slice(0, 4).join('\n') : poem.content;
            
            return (
              <div key={poem.id || poem._id} className="bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow border-2 border-gray-400" style={{ boxShadow: '0 0 15px rgba(156, 163, 175, 0.5), 0 0 30px rgba(156, 163, 175, 0.3)' }}>
                {poem.image && (
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={poem.image.startsWith('http') ? poem.image : `${APP_CONFIG.getBaseServerUrl()}${poem.image}`}
                      alt={poem.title}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="p-6">
                  <div className="mb-3">
                    <span className="bg-gray-700 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {poem.category || 'General'}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">{poem.title}</h3>
                <div className="poetry-text text-gray-300 mb-4 text-sm leading-tight" style={{ height: '110px', overflow: 'hidden' }}>
                  {displayText.split('\n').map((line, i) => (
                    <div key={i} className="mb-0.5">
                      {line}
                    </div>
                  ))}
                </div>
                  {shouldShowReadMore && (
                  <div className="mb-4 text-left">
                    <span
                      onClick={() => setSelectedPoem(poem)}
                      className="text-white hover:text-gray-300 cursor-pointer text-sm transition-colors"
                    >
                      Read More →
                    </span>
                  </div>
                  )}
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>By {poem.author || 'Anonymous'}</span>
                  <span>{new Date(poem.createdAt?.toDate?.() || poem.createdAt).toLocaleDateString()}</span>
                </div>
                
                {isEditMode && (
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => handleEdit(poem)}
                      className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm border border-white"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(poem)}
                      className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm border border-white"
                    >
                      Delete
                    </button>
                  </div>
                )}
                </div>
              </div>
            );
          })}
          
          {safePoems.length === 0 && !loading && (
            <div className="col-span-full text-center py-16">
              <div className="max-w-md mx-auto">
                <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6 border border-gray-700">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">No Poetry Yet</h3>
                <p className="text-gray-300 mb-6">
                  We're working on adding beautiful poetry to our collection. Check back soon for inspiring verses!
                </p>
                {isEditMode && (
                <Link
                  to="/admin/poetry"
                  className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-white"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Poetry
                </Link>
                )}
                </div>
              </div>
          )}
        </div>

        {selectedPoem && (
          <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-2xl max-w-4xl max-h-[90vh] w-full overflow-hidden shadow-2xl border border-gray-700">
              <div className="bg-gray-800 p-6 text-white border-b border-gray-700">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="mb-2">
                      <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-medium">
                        {selectedPoem.category || 'General'}
                      </span>
                    </div>
                    <h2 className="text-3xl font-bold mb-2">{selectedPoem.title}</h2>
                    <div className="flex items-center space-x-4 text-purple-100">
                      <span>By {selectedPoem.author || 'Anonymous'}</span>
                      <span>•</span>
                      <span>{new Date(selectedPoem.createdAt?.toDate?.() || selectedPoem.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedPoem(null)}
                    className="text-white hover:text-gray-200 transition-colors p-2"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto max-h-[60vh]">
                {selectedPoem.image && (
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={selectedPoem.image}
                      alt={selectedPoem.title}
                      className="w-full h-64 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                
                <div className="p-8">
                  <div className="poetry-text text-white text-lg leading-relaxed whitespace-pre-line">
                    {selectedPoem.content}
                  </div>
                </div>
              </div>

              <div className="bg-gray-800 px-8 py-4 border-t border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-300">
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      Poetry
                    </span>
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      {selectedPoem.content.split('\n').length} lines
                    </span>
                  </div>
                  <button
                    onClick={() => setSelectedPoem(null)}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors border border-white"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />
      </div>
    </div>
  );
};

export default Poetry;
